import * as React from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface NavItemProps {
  icon?: React.ReactNode
  label: string
  count?: number
  selected?: boolean
  foldable?: boolean
  isOpen?: boolean
  onToggle?: () => void
  onClick?: () => void
  actions?: React.ReactNode
  href?: string
  className?: string
  children?: React.ReactNode
  type?: 'link' | 'tag' | 'object'
}

const NavItem = React.forwardRef<HTMLDivElement, NavItemProps>(
  (
    {
      icon,
      label,
      count,
      selected = false,
      foldable = false,
      isOpen = true,
      onToggle,
      onClick,
      actions,
      href,
      className,
      children,
      type = 'link',
    },
    ref
  ) => {
    const handleClick = (e: React.MouseEvent) => {
      if (foldable && onToggle) {
        e.preventDefault()
        onToggle()
      } else if (onClick) {
        onClick()
      }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (foldable && onToggle) {
          onToggle()
        } else if (onClick) {
          onClick()
        }
      }
    }

    const content = (
      <>
        {/* Fold chevron */}
        {foldable && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggle?.()
            }}
            className="flex items-center justify-center h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={isOpen ? 'Collapse' : 'Expand'}
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform duration-200',
                !isOpen && '-rotate-90'
              )}
            />
          </button>
        )}

        {/* Icon */}
        {icon && (
          <span className={cn('shrink-0', type === 'tag' && 'text-current')}>
            {icon}
          </span>
        )}

        {/* Label */}
        <span className="flex-1 truncate text-sm">{label}</span>

        {/* Count badge */}
        {count !== undefined && count > 0 && (
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {count}
          </span>
        )}

        {/* Hover actions */}
        {actions && (
          <span
            className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            {actions}
          </span>
        )}
      </>
    )

    const baseClasses = cn(
      'group flex items-center gap-2 px-3 py-1.5 min-h-[36px] rounded-md transition-all duration-150',
      'hover:bg-secondary cursor-pointer',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      selected && 'bg-secondary text-accent font-medium',
      !selected && 'text-muted-foreground',
      className
    )

    if (href) {
      return (
        <div ref={ref} className="relative">
          <a href={href} className={baseClasses}>
            {content}
          </a>
          {foldable && isOpen && children && (
            <div className="ml-4 mt-0.5 space-y-0.5">{children}</div>
          )}
        </div>
      )
    }

    return (
      <div ref={ref} className="relative">
        <div
          role="button"
          tabIndex={0}
          className={baseClasses}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          aria-expanded={foldable ? isOpen : undefined}
          aria-current={selected && !foldable ? 'page' : undefined}
          aria-label={label}
        >
          {content}
        </div>
        {foldable && isOpen && children && (
          <div className="ml-4 mt-0.5 space-y-0.5">{children}</div>
        )}
      </div>
    )
  }
)
NavItem.displayName = 'NavItem'

// Action button for nav items
export interface NavItemActionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode
}

const NavItemAction = React.forwardRef<HTMLButtonElement, NavItemActionProps>(
  ({ icon, className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'flex items-center justify-center h-6 w-6 min-h-[24px] min-w-[24px] rounded-sm',
          'text-muted-foreground hover:text-foreground hover:bg-secondary/70',
          'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className
        )}
        {...props}
      >
        {icon}
      </button>
    )
  }
)
NavItemAction.displayName = 'NavItemAction'

export { NavItem, NavItemAction }
